package com.arena_web.ui.layouts

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.navigation.BottomNavBar
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.sections.header.MobileHeaderAction
import com.arena_web.ui.sections.header.MobileHeaderTrailing
import com.arena_web.ui.sections.header.MobileTopBar
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Composable
fun MobilePageLayout(
    activeTab: SidebarTab? = null,
    onSportChanged: (() -> Unit)? = null,
    mobileReady: Boolean = false,
    // Pass a page title (e.g. "Feed") to replace the sport-selector pill in
    // the centre of the mobile top bar.
    pageTitle: String? = null,
    // Selects the icon + badge tint for the right-side action in the top bar.
    headerAction: MobileHeaderAction = MobileHeaderAction.Notification,
    // Trailing top-bar slot: avatar (Profile page only), chat shortcut
    // (Arena), or nothing (everywhere else — the default).
    headerTrailing: MobileHeaderTrailing = MobileHeaderTrailing.None,
    // Back arrow instead of the brand logo (drill-in pages like Chat).
    headerBack: Boolean = false,
    // Custom widget replacing the right-side action button (News mode toggle).
    headerActionSlot: (@Composable () -> Unit)? = null,
    // Show the light/dark theme button in the top bar (News only for now).
    showThemeButton: Boolean = false,
    // Hide the bottom nav for full-screen subviews (e.g. chat room/details
    // where the surface is dedicated to a single conversation).
    hideBottomNav: Boolean = false,
    // Hide the global top bar — same use-case as `hideBottomNav`: full-screen
    // subviews own their own header chrome.
    hideTopBar: Boolean = false,
    // When non-null, paints a solid surface behind the WHOLE layout (top bar
    // included). Overrides the default body composite (blue tint + noise).
    // Used by pages whose design ships a custom backdrop (e.g. the marketing
    // home gallery, which sits on flat var(--l-s3, #02040B)).
    surfaceColor: String? = null,
    // When true, drops the layout's 16-px horizontal safe-area gutter so child
    // content can paint edge-to-edge. The child is then responsible for its own
    // horizontal padding where it needs it.
    edgeToEdge: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .overflow(Overflow.Hidden)
            .styleModifier {
                // Anchor to the visible viewport via `position: fixed` so
                // the layout is always pinned to the exact visible area,
                // regardless of dynamic browser chrome (URL bar, dynamic
                // island, keyboard). `100vh`/`100dvh` alone don't help on
                // older iOS where the page can still scroll up under the
                // address bar; fixed positioning locks it.
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("right", "0")
                property("bottom", "0")
                // Fallback for browsers that don't know `dvh` (Safari < 16).
                property("height", "100vh")
                property("height", "100dvh")
                // Per-page backdrop override — sits in the same paint layer as
                // the top bar, so the chrome adopts the same colour. Without
                // this the body composite shows through behind MobileTopBar.
                if (surfaceColor != null) {
                    property("background-color", surfaceColor)
                    // Light theme: aurora backdrop (light-only var; dark
                    // resolves `none` and stays a flat surface).
                    property("background-image", "var(--l-page-bg-image, none)")
                    property("background-repeat", "var(--arena-bg-repeat)")
                }
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    // Pad past the iPhone notch / Android status bar via the
                    // CSS `env()` safe-area variables. When the global top
                    // bar is hidden the child screen owns its own header
                    // chrome (back button etc.) and wants to manage the top
                    // breathing room INSIDE its scroll container, so we drop
                    // the extra 12 px and rely only on the safe-area inset.
                    val topPad = when {
                        // Full-bleed subview (e.g. a hero photo): the child paints
                        // up under the status bar and owns the safe-area inset for
                        // its own chrome, so the layout adds no top padding.
                        hideTopBar && edgeToEdge -> "0px"
                        hideTopBar -> "env(safe-area-inset-top, 0px)"
                        else -> "calc(env(safe-area-inset-top, 0px) + 12px)"
                    }
                    property("padding-top", topPad)
                    // Edge-to-edge pages opt out of the 16-px gutter (only the
                    // physical safe-area inset is preserved so iOS landscape
                    // doesn't push content under the notch).
                    if (edgeToEdge) {
                        property("padding-left", "env(safe-area-inset-left, 0px)")
                        property("padding-right", "env(safe-area-inset-right, 0px)")
                    } else {
                        property("padding-left", "max(16px, env(safe-area-inset-left, 0px))")
                        property("padding-right", "max(16px, env(safe-area-inset-right, 0px))")
                    }
                    property("gap", "16px")
                }
        ) {
            // Top bar (skipped for full-screen subviews like the chat room
            // or chat details which render their own header chrome).
            if (!hideTopBar) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("flex-shrink", "0")
                            // Edge-to-edge mode strips the layout's 16-px gutter
                            // so child content can paint full-bleed — but the top
                            // bar still wants its chrome to keep clear of the
                            // screen edges. Restore the gutter just for the bar.
                            if (edgeToEdge) {
                                property("padding-left", "max(16px, env(safe-area-inset-left, 0px))")
                                property("padding-right", "max(16px, env(safe-area-inset-right, 0px))")
                            }
                        }
                ) {
                    MobileTopBar(
                        onSportChanged = { sportName ->
                            onSportChanged?.invoke()
                        },
                        pageTitle = pageTitle,
                        headerAction = headerAction,
                        trailing = headerTrailing,
                        showBack = headerBack,
                        actionSlot = headerActionSlot,
                        showThemeButton = showThemeButton,
                    )
                }
            }

            if (mobileReady) {
                // Page content fills remaining height; the page itself owns
                // the bottom padding needed to clear the fixed BottomNavBar.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                ) {
                    content()
                }
            } else {
                // Coming Soon placeholder — this page's mobile version isn't ready yet
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .padding(bottom = 96.px),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = t.layoutComingSoon,
                        modifier = Modifier
                            .fontSize(28.px)
                            .fontWeight(FontWeight.Bold)
                            .color(org.jetbrains.compose.web.css.Color("var(--l-text-strong, #ffffff)"))
                    )
                }
            }
        }

        // Bottom navigation bar
        if (!hideBottomNav) {
            BottomNavBar(activeTab = activeTab)
        }
    }
}
